import { toast } from '@/components/toast';
import { VoicePlayer } from '@/components/VoicePlayer';
import { CampaignFormValues } from '@/pages/campaign/CreatePage/types';
import { useAgentEditorStore } from '@/state/root';
import { escapeSsmlText } from '@d-id/ssml';
import styled from '@emotion/styled';
import { Editor } from '@tiptap/core';
import CharacterCount from '@tiptap/extension-character-count';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { isContainSpaces, isValidScriptField } from '../scriptFieldUtils';
import { AddBreak } from './AddBreak';
import { ScriptFieldExtension } from './ScriptFieldExtension';
import { getPreviewText } from './utils';

const StyledEditorContent = styled(EditorContent, {
    shouldForwardProp: prop => prop !== 'textTooLong' && prop !== 'hoveredTag',
})<{ textTooLong?: boolean; hoveredTag: string | null }>`
    height: 100%;
    max-height: 424px;
    min-height: 262px;

    display: flex;
    flex-direction: column-reverse;
    flex-wrap: nowrap;

    font-size: 16px;
    font-weight: 400;
    line-height: 24px;

    border-radius: 10px;
    border: 1px solid ${({ theme, textTooLong }) => (textTooLong ? '#ED254E !important' : theme.colors.black[300])};

    :focus-within {
        border-color: ${({ theme }) => theme.colors.black[700]};
    }

    .tiptap {
        border-radius: 10px;
        overflow-y: auto;
        outline: none;
        height: 100%;
        padding: 16px 16px 52px;
        background-color: white;

        color: ${({ theme }) => theme.colors.secondary.main};
        caret-color: ${({ theme }) => theme.colors.primary[900]};

        code {
            background-color: ${({ theme }) => theme.colors.primary.bgLight};
            color: ${({ theme }) => theme.colors.primary.main};
        }

        ${({ hoveredTag, theme }) =>
            hoveredTag &&
            `
            code[data-script-field="${hoveredTag}"] {
                background-color: #ffe1cb;
                color: ${theme.colors.primary.main};
                font-weight: unset;
            }
        `}
    }
`;

const Controls = styled.div<{ textTooLong?: boolean }>`
    display: flex;
    padding: 14px 24px 14px 16px;
    background-color: ${({ theme }) => theme.colors.black[100]};
    position: relative;
    align-items: center;
    border-radius: 10px;
    min-height: 68px;
`;

const TextLength = styled.span<{ textTooLong?: boolean }>`
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${({ textTooLong, theme }) => (textTooLong ? theme.colors.error.main : theme.colors.black[700])};
    margin-left: auto;
`;

const MAX_CHARACTERS = 1200;

interface Props {
    hoveredTag: string | null;
    editorRef: React.MutableRefObject<Editor | null>;
}

export const RichEditor = observer(({ hoveredTag, editorRef }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const { getValues, control, setValue } = useFormContext<CampaignFormValues>();

    const { field: scriptField } = useController({
        name: 'script',
        control,
    });
    const { field: scriptHtml } = useController({
        name: 'script_html',
        control,
    });
    const previewText = useMemo(() => getPreviewText(scriptField.value), [scriptField.value]);

    const handleNewTag = (newTag: string) => {
        const currentTags = getValues('script_fields');
        const currentTagsSet = new Set(currentTags);

        currentTagsSet.add(newTag);
        setValue('script_fields', Array.from(currentTagsSet), { shouldValidate: true, shouldDirty: true });
    };

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ code: false }),
            CharacterCount,
            ScriptFieldExtension.configure({
                handleAddScriptField: handleNewTag,
                isValidScriptField: field => isValidScriptField(field) && !isContainSpaces(field),
                onInvalidScriptField: () => {
                    toast.error({
                        message: 'Script fields can only contain alphanumeric characters and underscores',
                    });
                },
            }),
        ],
        content: scriptHtml.value,
        parseOptions: {
            preserveWhitespace: 'full',
        },
        onUpdate: ({ editor }) => {
            const parsed = editor.getText({
                textSerializers: {
                    scriptField: ({ node }) => {
                        return `{{${node.attrs['data-script-field']}}}`;
                    },
                },
            });
            const escaped = escapeSsmlText(parsed);

            scriptHtml.onChange(editor.getHTML());
            scriptField.onChange(escaped);
        },
    });

    useEffect(() => {
        editorRef.current = editor;

        return () => {
            editorRef.current = null;
        };
    }, [editor]);

    if (!editor) {
        return null;
    }

    const handleAddBreak = () => {
        editor.commands.insertContentAt(editor.state.selection.from, ' ⏱ ', {
            updateSelection: true,
            parseOptions: {
                preserveWhitespace: 'full',
            },
        });
    };

    const textTooLong = editor.storage.characterCount.characters() > MAX_CHARACTERS;

    const { ttsSelectedVoice: voice } = agentEditorStore;

    return (
        <StyledEditorContent editor={editor} textTooLong={textTooLong} id="campaign-script-editor" hoveredTag={hoveredTag}>
            <Controls textTooLong={textTooLong}>
                <AddBreak voice={voice} onClick={handleAddBreak} />
                {voice && <VoicePlayer voice={voice} text={previewText || ''} />}

                <TextLength textTooLong={textTooLong} id="campaign-script-length">
                    {editor.storage.characterCount.characters()}/{MAX_CHARACTERS} characters
                </TextLength>
            </Controls>
        </StyledEditorContent>
    );
});
